// =============================================================================
// PEDRA — src/lib/format.ts
// Formatacao pt-BR (numeros, contagens, datas) e rotulos de dominio.
//
// Tudo que envolve MEDIDA (Micron) mora em `core/units` — nao duplicar aqui.
// Este arquivo cuida do que sobra: contagens, datas, nomes de arquivo, textos
// de enum e pequenos utilitarios de string.
// =============================================================================

// `trimTrailingZeros` mora em `core/units` (e onde nasce o texto de medida) e
// e reexportado aqui so por conveniencia de quem ja importa este modulo — a
// implementacao continua unica.
import { trimTrailingZeros } from '../core/units';
export { trimTrailingZeros };

import type {
  CutAxis,
  CutOrientation,
  DimensionOrder,
  GrainOrientation,
  OptimizationPriority,
  StackMode,
  UnfitReason,
} from '../core/types';

// -----------------------------------------------------------------------------
// NUMEROS — Intl.NumberFormat('pt-BR')
// -----------------------------------------------------------------------------

/** Cache de formatadores: criar `Intl.NumberFormat` e caro, reutilizamos. */
const numberFormatters = new Map<string, Intl.NumberFormat>();

function getFormatter(min: number, max: number): Intl.NumberFormat | null {
  const key = `${min}:${max}`;
  const cached = numberFormatters.get(key);
  if (cached) return cached;
  try {
    const f = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      useGrouping: true,
    });
    numberFormatters.set(key, f);
    return f;
  } catch {
    // Ambiente sem ICU completo: cai no formatador manual.
    return null;
  }
}

/** Agrupamento manual de milhares com ponto — usado se `Intl` falhar. */
function manualFormat(value: number, decimals: number): string {
  const negative = value < 0;
  const scale = 10 ** Math.max(0, Math.trunc(decimals));
  const n = Math.round(Math.abs(value) * scale);
  const intPart = String(Math.floor(n / scale));
  const frac = n % scale;
  let grouped = '';
  for (let i = 0; i < intPart.length; i++) {
    if (i > 0 && (intPart.length - i) % 3 === 0) grouped += '.';
    grouped += intPart[i];
  }
  let out = grouped;
  if (decimals > 0) out += ',' + String(frac).padStart(decimals, '0');
  return (negative && n !== 0 ? '-' : '') + out;
}

/**
 * Numero decimal em pt-BR: `formatDecimal(1234.5, 2)` -> `"1.234,50"`.
 * `decimals` fixa as casas (minimo = maximo).
 */
export function formatDecimal(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '—';
  const dp = Math.max(0, Math.min(9, Math.trunc(decimals)));
  const f = getFormatter(dp, dp);
  return f ? f.format(value) : manualFormat(value, dp);
}

/** Contagem inteira em pt-BR: `formatCount(1234)` -> `"1.234"`. */
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const v = Math.round(n);
  const f = getFormatter(0, 0);
  return f ? f.format(v) : manualFormat(v, 0);
}

/**
 * Numero "solto" com no maximo `decimals` casas, sem zeros a direita
 * desnecessarios: `2` -> `"2"`, `2.5` -> `"2,5"`, `3.07` -> `"3,07"`.
 */
export function formatLoose(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '—';
  const dp = Math.max(0, Math.min(9, Math.trunc(decimals)));
  const f = getFormatter(0, dp);
  if (f) return f.format(value);
  return trimTrailingZeros(manualFormat(value, dp));
}

/** Duracao em milissegundos -> `"820 ms"` / `"8,4 s"` / `"1 min 12 s"`. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${formatCount(ms)} ms`;
  if (ms < 60_000) return `${formatDecimal(ms / 1000, 1)} s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms - min * 60_000) / 1000);
  return `${formatCount(min)} min ${formatCount(sec)} s`;
}

// -----------------------------------------------------------------------------
// DATAS — montadas a mao para nao depender da variacao de ICU entre ambientes
// -----------------------------------------------------------------------------

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** `"30/07/2026"`. */
export function formatDate(d: Date): string {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** `"16:05"`. */
export function formatTime(d: Date): string {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** `"30/07/2026 16:05"`. */
export function formatDateTime(d: Date): string {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  return `${formatDate(d)} ${formatTime(d)}`;
}

/** Carimbo ordenavel para nome de arquivo: `"2026-07-30"`. */
export function formatFileStamp(d: Date): string {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '0000-00-00';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// -----------------------------------------------------------------------------
// TEXTO
// -----------------------------------------------------------------------------

/** Escolhe singular ou plural. Devolve apenas a palavra. */
export function pluralize(n: number, one: string, many: string): string {
  return Math.abs(n) === 1 ? one : many;
}

/** `"1.234 peças"` — contagem + palavra flexionada. */
export function formatQuantity(n: number, one: string, many: string): string {
  return `${formatCount(n)} ${pluralize(n, one, many)}`;
}

/**
 * Converte um nome livre em algo seguro para nome de arquivo:
 * sem acento, minusculo, separado por hifen, no maximo 60 caracteres.
 * Nunca devolve string vazia (cai em `"projeto"`).
 */
export function slugifyFilename(name: string): string {
  const base = (name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  return base === '' ? 'projeto' : base;
}

/** Corta o texto acrescentando reticencias quando passa de `max`. */
export function truncate(s: string, max: number): string {
  const text = s ?? '';
  const limit = Math.max(1, Math.trunc(max));
  if (text.length <= limit) return text;
  return text.slice(0, limit - 1).trimEnd() + '…';
}

/** `true` -> `"Sim"` · `false` -> `"Não"`. */
export function yesNo(v: boolean): string {
  return v ? 'Sim' : 'Não';
}

/** Primeira letra maiuscula, resto intocado. */
export function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Normaliza texto para comparacao: sem acento, minusculo, espacos colapsados.
 * Usado no casamento de cabecalhos de CSV e de tokens de veio/booleano.
 */
export function normalizeText(s: string): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// -----------------------------------------------------------------------------
// ROTULOS DE DOMINIO (pt-BR)
// -----------------------------------------------------------------------------

/** Direcao do veio da peca/chapa. */
export function grainLabel(o: GrainOrientation): string {
  switch (o) {
    case 1:
      return 'Ao longo da largura';
    case 2:
      return 'Ao longo do comprimento';
    default:
      return 'Indiferente';
  }
}

/** Rotulo curto do veio, para caber em celula de tabela. */
export function grainShortLabel(o: GrainOrientation): string {
  switch (o) {
    case 1:
      return 'Largura';
    case 2:
      return 'Comprimento';
    default:
      return '—';
  }
}

/** Objetivo principal da otimizacao. */
export function priorityLabel(p: OptimizationPriority): string {
  switch (p) {
    case 'leastCuts':
      return 'Menor número de cortes';
    case 'smallerSheets':
      return 'Usar as chapas menores';
    default:
      return 'Menor área desperdiçada';
  }
}

/** Preferencia de direcao do primeiro corte. */
export function cutOrientationLabel(c: CutOrientation): string {
  switch (c) {
    case 'preferWidth':
      return 'Preferir cortes ao longo da largura';
    case 'preferLength':
      return 'Preferir cortes ao longo do comprimento';
    default:
      return 'Ideal';
  }
}

/** Politica de agrupamento de chapas identicas. */
export function stackModeLabel(m: StackMode): string {
  switch (m) {
    case 'never':
      return 'Nunca';
    case 'always':
      return 'Sempre';
    default:
      return 'Automático';
  }
}

/** Ordem de exibicao das dimensoes. */
export function dimensionOrderLabel(d: DimensionOrder): string {
  return d === 'widthFirst' ? 'Largura × Comprimento' : 'Comprimento × Largura';
}

/** Motivo pelo qual a peca nao coube. */
export function unfitReasonLabel(r: UnfitReason): string {
  switch (r) {
    case 'tooLarge':
      return 'Maior que qualquer chapa disponível';
    case 'grainConflict':
      return 'Só caberia girada, mas o veio não permite';
    case 'noMaterialStock':
      return 'Não há chapa desse material no estoque';
    case 'stockExhausted':
      return 'Acabaram as chapas do estoque';
    default:
      return 'Medidas ou quantidade inválidas';
  }
}

/** Eixo do corte, na linguagem do operador. */
export function cutAxisLabel(a: CutAxis): string {
  return a === 'x' ? 'Vertical' : 'Horizontal';
}
